import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import {
  HidProxyInstance,
  JobsiteUpdateInput,
} from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import {
  getBadgeTemplateOptions,
  getHidFargoEntityOptions,
} from 'containers/jobsiteConfiguration/helpers/utils';
import {
  BadgeTemplateOptionElement,
  EditJobsiteConfigurationFormData,
  HidFargoSelectOptionElement,
} from 'containers/jobsiteConfiguration/types';
import { Jobsite, UserData } from './types';
import {
  getHidFargoConnectSectionDefaultValues,
  getHidFargoConnectSectionInputsHook,
  getHidFargoConnectSectionUpdateInput,
  hidProxyInstanceSelectOptions,
} from './hidFargoConnectSection';

export type EditJobsiteConfigTabFormData = Pick<
  EditJobsiteConfigurationFormData,
  'hidFargoConnect'
>;

export type JobsiteSelectOptions = {
  badgeTemplateOptions: BadgeTemplateOptionElement[];
  temporaryBadgeTemplateOptions: BadgeTemplateOptionElement[];
  hidProxyInstanceOptions: SelectOptionElement<HidProxyInstance>[];
  hidOrganizationOptions: HidFargoSelectOptionElement[];
  hidLocationOptions: HidFargoSelectOptionElement[];
  hidPrinterOptions: HidFargoSelectOptionElement[];
};

export const getDropdownsOptions = (args: {
  userData: UserData;
  jobsite: Jobsite;
}): JobsiteSelectOptions => {
  const { userData } = args;
  const allHidEntityOptions =
    userData?.hidEntityOptions.edges.map(({ node }) => node) ?? [];
  const availableBadgeTemplates =
    userData?.badgeTemplates.edges.map(({ node }) => node) ?? [];

  return {
    badgeTemplateOptions: getBadgeTemplateOptions(
      availableBadgeTemplates,
      false,
    ),
    temporaryBadgeTemplateOptions: getBadgeTemplateOptions(
      availableBadgeTemplates,
      true,
    ),
    hidProxyInstanceOptions: hidProxyInstanceSelectOptions,
    hidOrganizationOptions: getHidFargoEntityOptions(
      allHidEntityOptions,
      'Organization',
    ),
    hidLocationOptions: getHidFargoEntityOptions(
      allHidEntityOptions,
      'Location',
    ),
    hidPrinterOptions: getHidFargoEntityOptions(allHidEntityOptions, 'Printer'),
  };
};

type GetFormInputsArgs = JobsiteSelectOptions & {
  jobsite: Jobsite;
};

export const getFormInputs = (
  args: GetFormInputsArgs,
): TypedFormInputs<EditJobsiteConfigTabFormData> => {
  const {
    hidProxyInstanceOptions,
    hidOrganizationOptions,
    hidLocationOptions,
    hidPrinterOptions,
    badgeTemplateOptions,
    temporaryBadgeTemplateOptions,
  } = args;

  const hidFargoConnectSectionInputs = getHidFargoConnectSectionInputsHook({
    proxyInstanceOptions: hidProxyInstanceOptions,
    organizationOptions: hidOrganizationOptions,
    locationOptions: hidLocationOptions,
    printerOptions: hidPrinterOptions,
    badgeTemplateOptions,
    temporaryBadgeTemplateOptions,
  });

  return {
    hidFargoConnect: {
      element: FormInputTypes.Section,
      layout: '',
      elementProps: {
        title: 'HID Fargo Connect',
      },
      children: hidFargoConnectSectionInputs,
    },
  };
};

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
} & JobsiteSelectOptions;

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const {
    jobsite,
    hidProxyInstanceOptions,
    hidOrganizationOptions,
    hidLocationOptions,
    hidPrinterOptions,
    badgeTemplateOptions,
    temporaryBadgeTemplateOptions,
  } = args;
  const hidFargoConnect = getHidFargoConnectSectionDefaultValues(
    jobsite,
    hidProxyInstanceOptions,
    hidOrganizationOptions,
    hidLocationOptions,
    hidPrinterOptions,
    badgeTemplateOptions,
    temporaryBadgeTemplateOptions,
  );
  return { hidFargoConnect };
};

export const getJobsiteUpdateInput = async (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): Promise<JobsiteUpdateInput> => {
  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    ...getHidFargoConnectSectionUpdateInput(
      data.hidFargoConnect,
      dirtyFields.hidFargoConnect,
    ),
  });
};
